/**
 * Generic IndexedDB-backed mutation queue.
 * Each app creates its own MutationQueue with a unique database name.
 */

import { openDatabase, getStore, promisify } from '../db/open';
import type { QueuedMutation, MutationStatus, EnqueueOptions } from './types';
import { newClientId } from '../ids/newClientId';

const STORE_NAME = 'mutations';
const DB_VERSION = 1;

/** Monotonic enqueue counter for this page load (see QueuedMutation.seq). */
let enqueueSeq = 0;

/** FIFO: oldest first, enqueue order breaking a same-millisecond tie. */
const byAge = (a: QueuedMutation, b: QueuedMutation) => a.createdAt - b.createdAt || (a.seq ?? 0) - (b.seq ?? 0);

export class MutationQueue {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string) {
    this.dbName = dbName;
  }

  private getDB(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDatabase(this.dbName, DB_VERSION, (db) => {
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, { keyPath: 'id' });
          store.createIndex('status', 'status', { unique: false });
          store.createIndex('type', 'type', { unique: false });
          store.createIndex('createdAt', 'createdAt', { unique: false });
        }
      }).then((db) => {
        // If another tab needs to upgrade this DB, close our connection so it
        // isn't blocked; drop the cache so the next call reopens at the new version.
        db.onversionchange = () => {
          db.close();
          this.dbPromise = null;
        };
        return db;
      });
    }
    return this.dbPromise;
  }

  /** Add a mutation to the queue. Returns the generated ID.
   * If options.groupKey is set, any still-`pending` mutations sharing that key
   * are superseded (deleted) atomically, in the SAME transaction as the write,
   * so concurrent (un-awaited) enqueues of the same group cannot race to delete
   * each other. A `syncing` mutation is not in the `pending` index and is left
   * to finish. Blobs are intentionally NOT touched here — a superseded
   * mutation's blobs must be owner-scoped (PhotoStore ownerKey) and evicted by
   * the consumer via deleteBlobsByOwner at a terminal point (see EnqueueOptions). */
  async enqueue<T>(type: string, payload: T, options: EnqueueOptions = {}): Promise<string> {
    const id = newClientId();
    const now = Date.now();
    const groupKey = options.groupKey || null;
    const entry: QueuedMutation<T> = {
      id,
      type,
      payload,
      status: 'pending',
      createdAt: now,
      seq: ++enqueueSeq,
      updatedAt: now,
      retryCount: 0,
      maxRetries: options.maxRetries ?? 5,
      error: null,
      blobKeys: options.blobKeys ?? [],
      retainBlobs: options.retainBlobs ?? false,
      groupKey,
    };
    const db = await this.getDB();
    return new Promise<string>((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      store.put(entry);
      if (groupKey) {
        const cursorReq = store.index('status').openCursor('pending');
        cursorReq.onsuccess = () => {
          const cursor = cursorReq.result;
          if (cursor) {
            const m = cursor.value as QueuedMutation;
            if (m.groupKey === groupKey && m.id !== id) {
              cursor.delete();
            }
            cursor.continue();
          }
        };
      }
      tx.oncomplete = () => resolve(id);
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Get all mutations (any status) belonging to a supersede group.
   * Full-store scan (no groupKey index) — fine for the small per-owner queues
   * this is used for; add an index if call volume grows. */
  async getByGroup(groupKey: string): Promise<readonly QueuedMutation[]> {
    const db = await this.getDB();
    const all = await promisify(
      getStore(db, STORE_NAME, 'readonly').getAll(),
    ) as QueuedMutation[];
    return all.filter((m) => m.groupKey === groupKey);
  }

  /** Get all mutations with a given status, ordered by createdAt. */
  async getByStatus(status: MutationStatus): Promise<readonly QueuedMutation[]> {
    const db = await this.getDB();
    const index = getStore(db, STORE_NAME, 'readonly').index('status');
    const results = await promisify(index.getAll(status));
    return results.sort(byAge);
  }

  /** Get a single mutation by ID. */
  async get(id: string): Promise<QueuedMutation | undefined> {
    const db = await this.getDB();
    return promisify(getStore(db, STORE_NAME, 'readonly').get(id));
  }

  /** Update a mutation's status. Increments retryCount on failure.
   * Uses a single transaction for the read-modify-write to avoid auto-commit races. */
  async updateStatus(
    id: string,
    status: MutationStatus,
    error?: string,
  ): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      const getReq = store.get(id);
      getReq.onsuccess = () => {
        const existing = getReq.result as QueuedMutation | undefined;
        if (!existing) { resolve(); return; }
        const updated: QueuedMutation = {
          ...existing,
          status,
          updatedAt: Date.now(),
          error: error ?? null,
          retryCount: status === 'failed' || (status === 'pending' && existing.status === 'syncing')
            ? existing.retryCount + 1
            : existing.retryCount,
        };
        store.put(updated);
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Rewrite the payload of every queued row (any status but 'syncing') through `transform`,
   * in ONE transaction. A row is written only when `transform` returns a different value
   * (`!==`), so a pass over rows that need nothing writes nothing. Id, status, retryCount,
   * error and both timestamps are kept: this is a content scrub, not a state change, and it
   * must not postpone a stale-row reap or a 30-day expiry. A 'syncing' row is skipped: its
   * send already read the payload. Returns how many rows were rewritten.
   *
   * `transformError`, when given, is applied to a row's stored `error` text too (a failed
   * send's message can quote what the payload held); same rule, written only if it changed.
   */
  async rewritePayloads(
    transform: (payload: unknown) => unknown,
    transformError?: (error: string) => string,
  ): Promise<number> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      let rewritten = 0;
      let failure: unknown = null;
      const cursorReq = tx.objectStore(STORE_NAME).openCursor();
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (!cursor) return;
        const row = cursor.value as QueuedMutation;
        if (row.status !== 'syncing') {
          let next: unknown;
          let nextError = row.error;
          try {
            next = transform(row.payload);
            if (transformError && typeof row.error === 'string') nextError = transformError(row.error);
          } catch (err) {
            // A throwing transform rewrites nothing: abort the whole pass.
            failure = err;
            tx.abort();
            return;
          }
          if (next !== row.payload || nextError !== row.error) {
            // Only touch `error` when it changed: a row without one stays byte-identical.
            cursor.update(nextError === row.error ? { ...row, payload: next } : { ...row, payload: next, error: nextError });
            rewritten++;
          }
        }
        cursor.continue();
      };
      tx.oncomplete = () => resolve(rewritten);
      tx.onerror = () => reject(tx.error ?? new Error('rewritePayloads aborted'));
      tx.onabort = () => reject(failure ?? tx.error ?? new Error('rewritePayloads aborted'));
    });
  }

  /**
   * Reset every failed mutation to pending with a fresh retry budget.
   * Nothing else ever moves a mutation out of 'failed', so without this the
   * "Tap to retry" affordance was a no-op and exhausted mutations sat
   * unrecoverable in IndexedDB forever (2026-07 survey audit, finding A4).
   */
  async requeueFailed(): Promise<number> {
    const failed = await this.getByStatus('failed');
    if (failed.length === 0) return 0;
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      for (const mutation of failed) {
        store.put({
          ...mutation,
          status: 'pending' as MutationStatus,
          retryCount: 0,
          error: null,
          updatedAt: Date.now(),
        });
      }
      tx.oncomplete = () => resolve(failed.length);
      tx.onerror = () => reject(tx.error);
    });
  }

  /**
   * Reap mutations stranded in 'syncing' by a tab that was killed mid-drain.
   * `sync()` only reads 'pending' and `requeueFailed()` only 'failed', so a
   * mutation left 'syncing' when the tab died is otherwise invisible forever —
   * a silent write loss (Attack 7). Reset every 'syncing' row whose `updatedAt`
   * is older than `olderThanMs` back to 'pending' so the next drain picks it up.
   *
   * The age guard is load-bearing: it avoids clobbering a mutation another tab
   * is genuinely draining right now (a fresh 'syncing' row). `retryCount` is
   * preserved (unlike requeueFailed's user-initiated reset) so a row that keeps
   * getting stranded still eventually exhausts its retry budget instead of
   * looping forever.
   */
  async requeueStale(olderThanMs: number): Promise<number> {
    const syncing = await this.getByStatus('syncing');
    const cutoff = Date.now() - olderThanMs;
    const stale = syncing.filter((m) => m.updatedAt < cutoff);
    if (stale.length === 0) return 0;
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const store = tx.objectStore(STORE_NAME);
      for (const mutation of stale) {
        store.put({
          ...mutation,
          status: 'pending' as MutationStatus,
          error: null,
          updatedAt: Date.now(),
        });
      }
      tx.oncomplete = () => resolve(stale.length);
      tx.onerror = () => reject(tx.error);
    });
  }

  /** Delete a mutation from the queue. */
  async delete(id: string): Promise<void> {
    const db = await this.getDB();
    await promisify(getStore(db, STORE_NAME, 'readwrite').delete(id));
  }

  /** Get every mutation, any status. Used by the orphan blob sweep to find
   * every blobKey still referenced by SOME row before deleting anything. */
  async getAll(): Promise<readonly QueuedMutation[]> {
    const db = await this.getDB();
    return promisify(getStore(db, STORE_NAME, 'readonly').getAll());
  }

  /** Count mutations, optionally filtered by status. */
  async count(status?: MutationStatus): Promise<number> {
    if (status) {
      const items = await this.getByStatus(status);
      return items.length;
    }
    const db = await this.getDB();
    return promisify(getStore(db, STORE_NAME, 'readonly').count());
  }

  /** Clear all mutations with a given status, or all if no status specified.
   * Uses a single transaction with cursor for status-filtered clears. */
  async clear(status?: MutationStatus): Promise<void> {
    const db = await this.getDB();
    if (!status) {
      await promisify(getStore(db, STORE_NAME, 'readwrite').clear());
      return;
    }
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, 'readwrite');
      const index = tx.objectStore(STORE_NAME).index('status');
      const cursorReq = index.openCursor(status);
      cursorReq.onsuccess = () => {
        const cursor = cursorReq.result;
        if (cursor) {
          cursor.delete();
          cursor.continue();
        }
      };
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}
